import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { SUBJECTS } from '../../../constants';

const ContentEditModal = ({ 
  isOpen, 
  onClose, 
  mode, 
  newContent, 
  setNewContent, 
  handleAddContent, 
  handleUpdateContent, 
  formErrors 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 my-6">
        {/* ヘッダー部分 - 固定 */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-purple-700">
              {mode === 'edit' ? 'コンテンツを編集' : '新しいコンテンツを追加'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* スクロール可能なコンテンツ部分 */}
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="content-title" className="block text-sm font-medium text-purple-700 mb-1">
                コンテンツタイトル
              </label>
              <input
                type="text"
                id="content-title"
                name="title"
                value={newContent.title}
                onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                placeholder="コンテンツのタイトルを入力"
                className="w-full p-2 border border-gray-300 rounded"
              />
              {formErrors.title && <p className="text-red-600 text-sm">{formErrors.title}</p>}
            </div>

            <div>
              <label htmlFor="content-subject" className="block text-sm font-medium text-purple-700 mb-1">
                教科
              </label>
              <select
                id="content-subject"
                name="subject"
                value={newContent.subject}
                onChange={(e) => setNewContent({ ...newContent, subject: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">教科を選択</option>
                {SUBJECTS.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
              {formErrors.subject && <p className="text-red-600 text-sm">{formErrors.subject}</p>}
            </div>

            <div>
              <label htmlFor="randomize-quizzes" className="block text-sm font-medium text-purple-700 mb-1">
                出題順序
              </label>
              <select
                id="randomize-quizzes"
                name="randomizeQuizzes"
                value={newContent.randomizeQuizzes ? 'random' : 'fixed'}
                onChange={(e) => setNewContent({ ...newContent, randomizeQuizzes: e.target.value === 'random' })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="fixed">固定順</option>
                <option value="random">ランダム</option>
              </select>
            </div>

            <div>
              <label htmlFor="free-navigation" className="block text-sm font-medium text-purple-700 mb-1">
                問題の移動制限
              </label>
              <select
                id="free-navigation"
                name="freeNavigation"
                value={newContent.freeNavigation ? 'free' : 'restricted'}
                onChange={(e) => setNewContent({ ...newContent, freeNavigation: e.target.value === 'free' })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="restricted">順方向のみ（戻れない）</option>
                <option value="free">自由に行き来可能</option>
              </select>
            </div>

            {newContent.freeNavigation ? (
              <div>
                <label htmlFor="content-time-limit" className="block text-sm font-medium text-purple-700 mb-1">
                  コンテンツ全体の制限時間（秒）
                </label>
                <input
                  type="number"
                  id="content-time-limit"
                  name="timeLimitPerContent"
                  value={newContent.timeLimitPerContent || ''}
                  onChange={(e) => setNewContent({ ...newContent, timeLimitPerContent: e.target.value ? parseInt(e.target.value, 10) : 0 })}
                  placeholder="例: 300 (5分)"
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            ) : (
              <div>
                <label htmlFor="time-limit" className="block text-sm font-medium text-purple-700 mb-1">
                  1問あたりの制限時間（秒）
                </label>
                <input
                  type="number"
                  id="time-limit"
                  name="timeLimitPerQuestion"
                  value={newContent.timeLimitPerQuestion || ''}
                  onChange={(e) => setNewContent({ ...newContent, timeLimitPerQuestion: e.target.value ? parseInt(e.target.value, 10) : 0 })}
                  placeholder="例: 30"
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            )}

            <div>
              <label htmlFor="answer-reveal-mode" className="block text-sm font-medium text-purple-700 mb-1">
                解答表示モード
              </label>
              <select
                id="answer-reveal-mode"
                name="answerRevealMode"
                value={newContent.answerRevealMode}
                onChange={(e) => setNewContent({ ...newContent, answerRevealMode: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="immediate">各問題解答後に表示</option>
                <option value="end">最後にまとめて表示</option>
              </select>
            </div>

            <div>
              <label htmlFor="quick-answer-mode" className="block text-sm font-medium text-purple-700 mb-1">
                回答方式（四択・正誤問題）
              </label>
              <select
                id="quick-answer-mode"
                name="quickAnswerMode"
                value={newContent.quickAnswerMode || 'standard'}
                onChange={(e) => setNewContent({ ...newContent, quickAnswerMode: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="standard">標準（回答ボタンを押して確定）</option>
                <option value="quick">クイック（選択と同時に回答確定）</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                ※ この設定は四択問題と正誤問題にのみ適用されます
              </p>
            </div>
          </div>
        </div>

        {/* フッター部分 - 固定 */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex justify-end gap-2">
            <button
              onClick={mode === 'edit' ? handleUpdateContent : handleAddContent}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              {mode === 'edit' ? '更新' : '追加'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              キャンセル
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ContentEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  newContent: PropTypes.object.isRequired,
  setNewContent: PropTypes.func.isRequired,
  handleAddContent: PropTypes.func.isRequired,
  handleUpdateContent: PropTypes.func.isRequired,
  formErrors: PropTypes.object.isRequired,
};

export default ContentEditModal; 